/**
 * Centralises every access to Zone.js internal (`__zone_symbol__*`) properties.
 *
 * These properties are an implementation detail of Zone.js — they are NOT a public
 * API, and they do NOT exist under Angular's zoneless change detection. Every method
 * here therefore fails safe: it returns `false` / `null` / does nothing when the
 * internals are absent, instead of throwing.
 *
 * This is the single place to change when migrating to zoneless change detection or
 * when Zone.js renames its internals: the rest of the plugin only ever talks to
 * `AllyZone`, never to `__zone_symbol__*` directly.
 */
export class AllyZone {
    /**
     * The prefix Zone.js uses for the properties it patches onto DOM nodes.
     */
    private static readonly PREFIX = "__zone_symbol__";

    /**
     * Returns the array of click handlers Zone.js patched onto a node, or `null`
     * when none exist (e.g. under zoneless change detection).
     * @param node The node to inspect.
     */
    public static getClickHandlers(node: unknown): { callback: unknown }[] | null {
        const handlers = (node as { [key: string]: unknown })?.[AllyZone.PREFIX + "clickfalse"];
        if (Array.isArray(handlers)) {
            return handlers as { callback: unknown }[];
        }
        return null;
    }

    /**
     * Checks whether a node has a Zone.js-patched click listener.
     *
     * Returns `false` under zoneless change detection (the activation feature then
     * simply does not apply, leaving the application's behaviour unchanged).
     * @param node The node to check.
     */
    public static hasClickListener(node: unknown): boolean {
        return AllyZone.getClickHandlers(node) !== null;
    }

    /**
     * Replaces the callback of the first Zone.js-patched click handler with a no-op,
     * suppressing it. Does nothing if the handler is absent.
     * @param node The node whose first click handler should be suppressed.
     */
    public static suppressFirstClickHandler(node: unknown): void {
        const handlers = AllyZone.getClickHandlers(node);
        if (handlers && handlers[0]) {
            handlers[0].callback = () => {};
        }
    }

    /**
     * Removes the residual Zone.js properties that are created when an event listener
     * is added to a node. Safe to call when the properties do not exist.
     * @param node The node to clean up.
     * @param eventType The event type whose residual properties should be removed.
     */
    public static cleanUpListener(node: unknown, eventType: string): void {
        const target = node as { [key: string]: unknown };
        if (!target) {
            return;
        }
        delete target[AllyZone.PREFIX + eventType + "false"];
        delete target[AllyZone.PREFIX + "ON_PROPERTY" + eventType];
    }
}
